import styles from "./Transactions.module.scss";
import useTransactions from "./useTransactions";
import clsx from "clsx";
import ColoredAmount from "../../../common/ColoredAmount";
import {useTranslation} from "react-i18next";
import React, {useEffect, useRef} from "react";
import {TransactionEditor} from "../transaction-editor/TransactionEditor";
import {MODE_EDIT, PORTFOLIO_TRANSACTION_TYPE_BUY, TRANSACTION_TYPE_TRANSFER} from "../../../codes";
import useFormat from "../../../util/useFormat";
import {TransactionsMenu} from "./TransactionsMenu";
import {TransactionsAccounts} from "./TransactionsAccounts";
import AutoSizer from "react-virtualized-auto-sizer";
import {VariableSizeList} from "react-window";
import {TransactionsToolbar} from "./TransactionsToolbar";
import useProject from "../../../util/useProject";
import {TransactionsFab} from "./TransactionsFab";
import {MileageLogEditor} from "../../cars/mileage-log-editor/MileageLogEditor";
import {PortfolioTransactionEditor} from "../../portfolio/portfolio-transaction-editor/PortfolioTransactionEditor";
import {Content, ContentFixed, ContentScroll} from "../../../common/Content";

const Transactions = () => {
  const {formatAmount, formatNumber, formatDate} = useFormat();
  const {t} = useTranslation("transactions");

  const {
    listKey,
    accounts,
    transactionCategories,
    transactionTags,
    transactions,
    selectedTransactions,
    toggleSelectedTransaction,
    toggleAllTransactions,
    showTransactionEditor,
    showMileageLogEditor,
    showPortfolioTransactionEditor,
    searchTag,
    cars,
    mileageLogs,
    portfolioTransactions
  } = useTransactions();

  const {runWithExpiration} = useProject();

  const Row = ({index, style}) => {
    const transaction = transactions[index];

    const selected = selectedTransactions[transaction.id] || false;
    const future = transaction.future;

    const account = accounts[transaction.account];

    let payeeComponent = <span>{transaction.payeeDisplay || transaction.payee}</span>;
    let amountComponent;
    let categoryComponent = <></>;

    if (transaction.type === TRANSACTION_TYPE_TRANSFER) {
      const transferAccount = accounts[transaction.payee];

      payeeComponent = t("transaction_type_transfer_" + (transaction.amount < 0 ? "out" : "in"), {account: transferAccount.name});
      amountComponent = <span className={styles.amount}>{formatAmount(transaction.amount, account.currency)}</span>;
    } else {
      amountComponent = <ColoredAmount className={styles.amount} amount={transaction.amount}>
        {formatAmount(transaction.amount, account.currency)}
      </ColoredAmount>;
    }

    if (transaction.category) {
      const category = transactionCategories[transaction.category];

      categoryComponent = <TransactionCategory category={category}/>;
    }

    let note = transaction.note;
    let mileageLog = mileageLogs[transaction.id];
    let portfolioTransaction = portfolioTransactions[transaction.id];

    if (mileageLog) {
      const car = cars[mileageLog.car];

      const [background, color] = car.color.split("|");

      payeeComponent = <span className={clsx(styles.tag, styles.carName)} style={{background, color, border: "1px solid " + background}}>{car.name}</span>;

      const parts = [];

      if (mileageLog.distance) {
        parts.push(formatNumber(mileageLog.odometer, "#T##0D##") + " " + car.odometerUnit + " (+" + formatNumber(mileageLog.distance, "#T##0D##") + " " + car.odometerUnit + ")");
      } else {
        parts.push(formatNumber(mileageLog.odometer, "#T##0D##") + " " + car.odometerUnit);
      }

      parts.push(formatNumber(mileageLog.quantity) + " " + car.quantityUnit + " @ " + formatAmount(mileageLog.price));

      if (mileageLog.conso) {
        parts.push(formatNumber(mileageLog.conso, "#T##0D000") + " " + car.consoUnit);
      }

      note = parts.join(" | ");
    } else if (portfolioTransaction) {
      const positiveNegativeStyle = portfolioTransaction.type === PORTFOLIO_TRANSACTION_TYPE_BUY
        ? styles.portfolioTransactionPositive
        : styles.portfolioTransactionNegative;

      payeeComponent = <span className={clsx(styles.tag, positiveNegativeStyle)}>
        {portfolioTransaction.type === PORTFOLIO_TRANSACTION_TYPE_BUY ? "BUY" : "SELL"} {portfolioTransaction.symbol}
      </span>;

      note = formatNumber(portfolioTransaction.quantity, "#T##0D##") + " @ " + formatAmount(portfolioTransaction.price)
        + (portfolioTransaction.note ? " | " + portfolioTransaction.note : "");
    }

    return <div
      style={style}
      className={clsx(styles.transaction, selected ? styles.selected : null, future ? styles.future : styles.past)}
    >
      <div className={styles.selector}>
        <span className={clsx("material-icons", styles.select)} onClick={() => toggleSelectedTransaction(transaction)}>
          {selected ? "check_box" : "check_box_outline_blank"}
        </span>
      </div>

      <div className={clsx(styles.details, "hover-selectable")} onClick={runWithExpiration(() => {
        if (mileageLogs[transaction.id]) {
          showMileageLogEditor(MODE_EDIT, mileageLogs[transaction.id]);
        } else if (portfolioTransactions[transaction.id]) {
          showPortfolioTransactionEditor(MODE_EDIT, portfolioTransactions[transaction.id]);
        } else {
          showTransactionEditor(MODE_EDIT, transaction);
        }
      })}>
        <div className={styles.transactionAccount}>
          <div className={styles.color} style={{background: account.color.split("|")[0]}}/>
          <div className={styles.name}>{account.name}</div>
        </div>
        <div className={styles.amountValueDate}>
          {amountComponent}
          <div className={styles.valueDate}>
            {formatDate(transaction.valueDate)}
          </div>
        </div>
        <div className={styles.payee}>
          <div className={styles.payeeContainer}>
            {transaction.tags && <span className={styles.tags}>{transaction.tags.map(t => {
                const tag = transactionTags[t];

                const [background, color] = tag?.color ? tag.color.split("|") : ["#DDD", "#333"];

                return <span className={clsx(styles.tag)} style={{background, color, border: "1px solid " + background}} key={t} onClick={e => {
                  e.stopPropagation();

                  searchTag(t);
                }}>
                  {t}
                </span>;
              }
            )}</span>}

            {payeeComponent}

            {note && <span className={styles.note}>{note}</span>}
          </div>
        </div>
        <div className={styles.category}>{categoryComponent}</div>
      </div>
    </div>;
  };

  const listRef = useRef();

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTo(0);
      listRef.current.scrollToItem(0, "start");
      listRef.current.resetAfterIndex(0);
    }
  }, [listKey]);

  let selectAllIcon;

  if (Object.keys(selectedTransactions).length === transactions.length) {
    selectAllIcon = "check_box";
  } else if (Object.keys(selectedTransactions).length > 0) {
    selectAllIcon = "indeterminate_check_box";
  } else {
    selectAllIcon = "check_box_outline_blank";
  }

  return (
    <Content>
      <ContentFixed>
        <TransactionsMenu transactions={transactions}/>

        <TransactionsAccounts/>

        <TransactionsToolbar transactions={transactions}/>

        <TransactionsFab/>

        <TransactionEditor/>
        <MileageLogEditor/>
        <PortfolioTransactionEditor/>

        <div className={clsx(styles.transaction, styles.header)}>
          <div className={styles.selector}>
            <span className={clsx("material-icons", styles.select)} onClick={toggleAllTransactions}>
              {selectAllIcon}
            </span>
          </div>

          <div className={styles.details}>
            <div className={styles.transactionAccount}>
              <div className={styles.name}>{t("list_header_account")}</div>
            </div>
            <div className={styles.amount}>{t("list_header_amount")}</div>
            <div className={styles.valueDate}>{t("list_header_date")}</div>
            <div className={styles.payee}>{t("list_header_payee")}</div>
            <div className={styles.category}>{t("list_header_category")}</div>
          </div>
        </div>
      </ContentFixed>

      <ContentScroll whiteBackground className={styles.transactions}>
        <AutoSizer>
          {({height, width}) => {
            return (<VariableSizeList
              className="custom-scrollbar autosizer-scrollbar"
              style={{overflowY: "scroll"}}
              ref={listRef}
              height={height - 0.5}
              itemCount={transactions.length}
              itemSize={() => width < 1130 ? 42 : 30}
              estimatedItemSize={width < 1130 ? 42 : 30}
              itemKey={index => transactions[index].id}
              width={width}
              overscanCount={10}
              useIsScrolling
            >
              {Row}
            </VariableSizeList>);
          }}
        </AutoSizer>
      </ContentScroll>
    </Content>
  );
};

const TransactionCategory = ({category}) => {
  const [background, color] = category.color.split("|");

  return <>
    <div className={styles.categoryColor} style={{background}}>
      <span className={clsx(styles.categoryIcon, "material-icons")} style={{color}}>{category.icon}</span>
    </div>

    <div className={styles.categoryName}>{category.groupName} / {category.name}</div>
  </>;
};

export default Transactions;
